/**
 * 创建一个增强的枚举对象，支持多种访问方式和实用方法
 * @param {Object} enumList - 枚举配置对象，键为枚举名，值为字符串或对象
 * @param {string|Object} enumList.* - 枚举值，可以是字符串或包含 txt 和 color 属性的对象
 * @param {string} [enumList.*.txt] - 当值为对象时的显示文本
 * @param {string} [enumList.*.color] - 当值为对象时的颜色属性，默认为 'default'
 * @returns {Proxy} 返回一个代理对象，具有以下特性：
 *   - 直接属性访问返回 txt 值（默认行为）
 *   - 支持访问具体属性（如 .txt、.color）
 *   - 包含 getList()、map()、getValues() 等实用方法
 *
 * @example
 * // 使用示例一：字符串值枚举
 * const statusEnum = enum_Transformed({
 *   ACTIVE: '激活',
 *   INACTIVE: '未激活',
 *   PENDING: '待处理'
 * });
 *
 * // 获取标签值对数组
 * console.log(statusEnum.getList());
 * // 输出: [{label: '激活', value: 'ACTIVE'}, {label: '未激活', value: 'INACTIVE'}, {label: '待处理', value: 'PENDING'}]
 *
 * // 获取所有键值
 * console.log(statusEnum.getValues());
 * // 输出: ["ACTIVE", "INACTIVE", "PENDING"]
 *
 * // 检查键是否存在
 * console.log(statusEnum.getValues('ACTIVE')); // 输出: true
 * console.log(statusEnum.getValues('UNKNOWN')); // 输出: false
 *
 * // 直接访问返回 txt 值
 * console.log(statusEnum.ACTIVE);        // 输出: "激活"
 * console.log(statusEnum['INACTIVE']);   // 输出: "未激活"
 *
 * // 用于循环遍历
 * console.log(statusEnum.map((item, value, enumObj) => {
 *   return `${item.label}: ${item.value}`;
 * }));
 * // 输出: ["激活: ACTIVE", "未激活: INACTIVE", "待处理: PENDING"]
 *
 * @example
 * // 使用示例二：对象值枚举（包含 txt 和 color）
 * const categoryEnum = enum_Transformed({
 *   NEWS: { txt: '新闻', color: 'blue' },
 *   TECH: { txt: '科技', color: 'green' },
 *   SPORTS: { txt: '体育', color: 'red' }
 * });
 *
 * // 获取标签值对数组
 * console.log(categoryEnum.getList());
 * // 输出: [{label: '新闻', value: 'NEWS'}, {label: '科技', value: 'TECH'}, {label: '体育', value: 'SPORTS'}]
 *
 * // 获取所有键值
 * console.log(categoryEnum.getValues());
 * // 输出: ["NEWS", "TECH", "SPORTS"]
 *
 * // 检查键是否存在
 * console.log(categoryEnum.getValues('NEWS')); // 输出: true
 * console.log(categoryEnum.getValues('UNKNOWN')); // 输出: false
 *
 * // 直接访问返回完整对象（因为 color 不是 'default'）
 * console.log(categoryEnum.NEWS);        // 输出: { txt: '新闻', color: 'blue' }
 *
 * // 访问具体属性
 * console.log(categoryEnum.NEWS.txt);    // 输出: "新闻"
 * console.log(categoryEnum.NEWS.color);  // 输出: "blue"
 */
const enum_Transformed = (enumList) => {
    const transformedEnum = Object.entries(enumList).reduce(
        (acc, [key, value]) => {
            // 如果值是个简单对象，我们为其添加默认值
            const processedValue =
                typeof value === "object"
                    ? value
                    : {
                          txt: value,
                          color: "default",
                      };
            acc[key] = processedValue;
            return acc;
        },
        {}
    );
    const enumObject = {
        ...transformedEnum,
        /**
    * 获取枚举的标签值对数组，适用于表单选择器
    * @returns {Array<{label: string, value: string}>} 返回包含 label 和 value 的对象数组
    * @description 自动处理字符串值和对象值，确保 label 不为 undefined
    * @example
    * const options = enumObj.getList();
    * // 返回: [{label: '激活', value: 'ACTIVE'}, {label: '未激活', value: 'INACTIVE'}]
    * 
    /**
     * 获取枚举的标签值对数组，适用于表单选择器
     * @returns {Array<{label: string, value: string}>} 返回包含 label 和 value 的对象数组
     * @description 自动处理字符串值和对象值，确保 label 不为 undefined
     * @example
     * const options = enumObj.getList();
     * // 返回: [{label: '激活', value: 'ACTIVE'}, {label: '未激活', value: 'INACTIVE'}]
     * 
     * // 字符串值也能正确获取 label
     * const stringEnum = enum_Transformed({ ACTIVE: '激活' });
     * stringEnum.getList(); // 返回: [{label: '激活', value: 'ACTIVE'}]
     */
        getList() {
            // 获取原始对象，绕过 Proxy 拦截
            const originalTarget = this._target || this;
            const result = Object.entries(originalTarget)
                .filter(
                    ([key]) =>
                        key !== "map" &&
                        key !== "getList" &&
                        key !== "getValues"
                )
                .map(([key, value]) => ({
                    label: typeof value === "string" ? value : value.txt,
                    value: key,
                }));
            return result;
        },
        /**
         * 遍历枚举并执行回调函数
         * @param {Function} callback - 回调函数，接收三个参数
         * @param {Object} callback.item - 当前项，包含 label 和 value 属性
         * @param {Object} callback.value - 原始枚举值对象
         * @param {Object} callback.enumObj - 枚举对象本身
         * @returns {Array} 返回回调函数处理后的数组
         * @throws {TypeError} 当 callback 不是函数时抛出错误
         * @example
         * const result = enumObj.map((item, value, enumObj) => {
         *   return `${item.label}: ${item.value}`;
         * });
         * // 返回: ["激活: ACTIVE", "未激活: INACTIVE"]
         */
        map(callback) {
            if (typeof callback !== "function") {
                throw new TypeError("Callback must be a function");
            }
            return Object.entries(this)
                .filter(
                    ([key]) =>
                        key !== "map" &&
                        key !== "getList" &&
                        key !== "getValues"
                )
                .map(([key, value]) =>
                    callback(
                        {
                            label:
                                typeof value === "string" ? value : value.txt,
                            value: key,
                        },
                        value,
                        this
                    )
                );
        },
        /**
         * 获取枚举的所有键值或检查指定键是否存在
         * @param {string} [checkValue] - 可选参数，要检查的键值
         * @returns {Array<string>|boolean} 无参数时返回所有键值数组，有参数时返回布尔值
         * @example
         * // 获取所有键值
         * const allKeys = enumObj.getValues();
         * // 返回: ["ACTIVE", "INACTIVE", "PENDING"]
         *
         * // 检查键是否存在
         * const exists = enumObj.getValues('ACTIVE');
         * // 返回: true
         *
         * const notExists = enumObj.getValues('UNKNOWN');
         * // 返回: false
         */
        getValues(checkValue) {
            if (checkValue !== undefined) {
                // 如果传入了参数，检查该值是否存在于枚举中
                const keys = Object.keys(this).filter(
                    (key) =>
                        key !== "map" &&
                        key !== "getList" &&
                        key !== "getValues"
                );
                return keys.includes(checkValue);
            }

            // 如果没有传入参数，返回所有键值
            return Object.keys(this)
                .filter(
                    (key) =>
                        key !== "map" &&
                        key !== "getList" &&
                        key !== "getValues"
                )
                .map((key) => key);
        },
    };

    // 使用 Proxy 来拦截属性访问，直接返回 txt 值
    return new Proxy(enumObject, {
        get(target, prop) {
            // 如果是方法调用，直接返回原方法
            if (typeof target[prop] === "function") {
                return target[prop];
            }

            // 处理特殊属性访问 - 直接返回键名映射对象
            if (prop === "_KEY") {
                const keyMap = {};
                Object.keys(transformedEnum).forEach((key) => {
                    keyMap[key] = key;
                });
                return keyMap;
            }
            // 如果是属性访问，检查是否存在该属性
            if (
                prop in target &&
                prop !== "map" &&
                prop !== "getList" &&
                prop !== "getValues"
            ) {
                // 如果属性是对象，并且包含 txt 和 color 属性，并且 color 不是 default，则返回对象
                if (
                    Object.keys(target[prop]).length === 2 &&
                    target[prop].color !== "default"
                ) {
                    return target[prop];
                }

                // 如果属性是对象，并且包含 txt 和 color 属性，并且 color 是 default，则返回 txt
                return target[prop].txt;
            }

            // 其他情况返回 undefined
            return undefined;
        },
    });
};
export default enum_Transformed;

// const cg = enum_Transformed({
//     DH: {
//         txt: '动画',
//         color: 'red'
//     },
//     MK: {
//         txt: '慕课',
//         color: 'blue'
//     }
// })

// console.log(cg)
// console.log(cg.DH.txt, cg.DH.color);
// console.log(cg.getList())
// console.log('===========')

// const dta = enum_Transformed({
//     DH: '动画',
//     MK: '慕课'
// })
// console.log(dta)
// console.log(dta.getList())
// console.log(dta.getValues())
// console.log(dta.getValues('DH'));
// console.log(dta['DH'])

/**********************************验证示例代码*****************************/

// // 使用示例一
// console.log('使用示例一')
// const statusEnum = enum_Transformed({
//     ACTIVE: '激活',
//     INACTIVE: '未激活',
//     PENDING: '待处理'
// });
// console.log("获取可迭代的数组：", statusEnum.getList()) // 输出: [{label: '激活', value: 'ACTIVE'}, {label: '未激活', value: 'INACTIVE'}, {label: '待处理', value: 'PENDING'}]
// console.log("获取所有键值：", statusEnum.getValues()) // 输出: ["ACTIVE", "INACTIVE", "PENDING"]
// console.log("检查键是否存在：", statusEnum.getValues('ACTIVE')) // 输出: true
// console.log("检查键是否存在：", statusEnum.getValues('UNKNOWN')) // 输出: false

// console.log("直接访问返回 txt 值：", statusEnum.ACTIVE);        // 输出: "激活"
// console.log("直接访问返回 txt 值：", statusEnum['INACTIVE']);   // 输出: "未激活"
// // 用于循环
// console.log("用于循环：", statusEnum.map((item, value, enumObj) => {
//     return `${item.label}: ${item.value}`;
// }));

// console.log('使用示例二')
// // 对象值 - 包含 txt 和 color
// const categoryEnum = enum_Transformed({
//     NEWS: { txt: '新闻', color: 'blue' },
//     TECH: { txt: '科技', color: 'green' },
//     SPORTS: { txt: '体育', color: 'red' }
// });

// console.log("获取可迭代的数组：", categoryEnum.getList()) // 输出: [{label: '新闻', value: 'NEWS'}, {label: '科技', value: 'TECH'}, {label: '体育', value: 'SPORTS'}]
// console.log("获取所有键值：", categoryEnum.getValues()) // 输出: ["NEWS", "TECH", "SPORTS"]
// console.log("检查键是否存在：", categoryEnum.getValues('NEWS')) // 输出: true
// console.log("检查键是否存在：", categoryEnum.getValues('UNKNOWN')) // 输出: false
// console.log("用于循环：", statusEnum.map((item, value, enumObj) => {
//     return `${item.label}: ${item.value}`;
// }));
// console.log("直接访问返回完整对象：", categoryEnum.NEWS);        //{ txt: '新闻', color: 'blue' }
// console.log("直接访问返回 txt 值：", categoryEnum.NEWS.txt);    // 输出: "新闻"
// console.log("直接访问返回 color 值：", categoryEnum.NEWS.color);  // 输出: "blue"
